const AUTH_REQUIRED_MESSAGE = "Sign in to use transcription and prompt features.";
const MIC_PERMISSION_MESSAGE = "Please allow microphone access to start recording.";
const MIC_NOT_FOUND_MESSAGE = "No microphone was found. Connect a microphone and try again.";
const SECURE_CONTEXT_MESSAGE = "Microphone access requires HTTPS or localhost.";
const NETWORK_MESSAGE = "Connection lost. Check your internet and try again.";
const TRANSCRIPTION_MESSAGE =
  "Transcription is temporarily unavailable. Please try again shortly.";
const PROMPT_MESSAGE =
  "Prompt processing is temporarily unavailable. Please try again shortly.";

const readStatus = (value) => value?.status || value?.response?.status || null;
const readErrorCode = (value) =>
  value?.errorCode ||
  value?.code ||
  value?.error?.errorCode ||
  value?.error?.code ||
  value?.response?.data?.errorCode ||
  null;
const readName = (value) => value?.name || null;
const readMessage = (value) =>
  String(
    value?.message ||
    value?.error?.message ||
    value?.response?.data?.error ||
    value?.response?.data?.message ||
    ""
  ).trim();

const isOffline = () =>
  typeof navigator !== "undefined" &&
  Object.prototype.hasOwnProperty.call(navigator, "onLine") &&
  navigator.onLine === false;

const matches = (message, pattern) => pattern.test(String(message || ""));

export const formatAppError = (value, { feature = "transcription" } = {}) => {
  const status = readStatus(value);
  const errorCode = readErrorCode(value);
  const name = readName(value);
  const message = readMessage(value);

  if (
    status === 401 ||
    status === 403 ||
    errorCode === "AUTH_REQUIRED" ||
    matches(message, /no auth cookies|sign in to continue|unable to fetch websocket token/i)
  ) {
    return AUTH_REQUIRED_MESSAGE;
  }

  if (
    errorCode === "MIC_PERMISSION_DENIED" ||
    name === "NotAllowedError" ||
    matches(message, /microphone.*denied|permission.*microphone|allow microphone/i)
  ) {
    return MIC_PERMISSION_MESSAGE;
  }

  if (
    errorCode === "MIC_NOT_FOUND" ||
    name === "NotFoundError" ||
    matches(message, /no microphone|microphone.*not found/i)
  ) {
    return MIC_NOT_FOUND_MESSAGE;
  }

  if (
    errorCode === "SECURE_CONTEXT_REQUIRED" ||
    matches(message, /https|secure context/i)
  ) {
    return SECURE_CONTEXT_MESSAGE;
  }

  if (
    isOffline() ||
    errorCode === "NETWORK_ERROR" ||
    matches(message, /network issue|connection error|websocket connection error|ws open timeout|timeout/i)
  ) {
    return NETWORK_MESSAGE;
  }

  if (
    errorCode === "PROMPT_UNAVAILABLE" ||
    (feature === "prompt" && matches(message, /prompt/i))
  ) {
    return PROMPT_MESSAGE;
  }

  if (
    errorCode === "TRANSCRIPTION_UNAVAILABLE" ||
    feature === "transcription" ||
    matches(message, /transcription|audio processing|realtime/i)
  ) {
    return TRANSCRIPTION_MESSAGE;
  }

  return message || "Something went wrong. Please try again.";
};

export const createSecureContextError = () => {
  const error = new Error("Mic requires HTTPS (or localhost) secure context.");
  error.errorCode = "SECURE_CONTEXT_REQUIRED";
  return error;
};
